import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

// 急救场景数据
const firstAidScenarios = [
  {
    title: '心肺复苏 (CPR)',
    subtitle: 'Cardiopulmonary Resuscitation',
    icon: '❤️',
    steps: [
      '确认现场安全',
      '检查意识和呼吸',
      '拨打急救电话 120',
      '胸外按压：30次',
      '人工呼吸：2次',
      '重复循环',
    ],
    color: '#EF4444',
    tip: '按压深度：5-6厘米，频率：100-120次/分钟',
  },
  {
    title: '止血包扎',
    subtitle: 'Bleeding Control',
    icon: '🩹',
    steps: [
      '戴上防护手套',
      '用干净纱布直接按压',
      '抬高受伤部位',
      '持续按压10-15分钟',
      '不要移除血浸的纱布',
      '必要时添加新纱布',
    ],
    color: '#F59E0B',
    tip: '严重出血需立即就医',
  },
  {
    title: '烧伤处理',
    subtitle: 'Burn Treatment',
    icon: '🔥',
    steps: [
      '用流动冷水冲洗',
      '冲洗时间：10-20分钟',
      '不要用冰块',
      '轻轻移除束缚物',
      '用无菌纱布覆盖',
      '不要挑破水泡',
    ],
    color: '#10B981',
    tip: '严重烧伤立即就医，不要涂牙膏或酱油',
  },
];

// 心跳动画组件
const HeartbeatPulse: React.FC<{ frame: number }> = ({ frame }) => {
  const pulse = Math.sin(frame * 0.3) * 0.1 + 1;
  const opacity = 0.3 + Math.sin(frame * 0.3) * 0.2;

  return (
    <div
      style={{
        position: 'absolute',
        width: 300,
        height: 300,
        background: `radial-gradient(circle, rgba(239, 68, 68, ${opacity}), transparent)`,
        borderRadius: '50%',
        transform: `scale(${pulse})`,
      }}
    />
  );
};

// 步骤卡片组件
const StepCard: React.FC<{
  step: string;
  index: number;
  frame: number;
  fps: number;
  color: string;
  startFrame: number;
}> = ({ step, index, frame, fps, color, startFrame }) => {
  const delay = startFrame + index * 10;
  const progress = spring({
    frame: frame - delay,
    fps,
    config: { damping: 12, stiffness: 200 },
  });

  const x = interpolate(progress, [0, 1], [-50, 0]);
  const opacity = progress;

  if (frame < delay) return null;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        marginBottom: 14,
        opacity,
        transform: `translateX(${x}px)`,
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          background: color,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 22,
          fontWeight: 'bold',
          color: 'white',
          boxShadow: `0 0 20px ${color}40`,
        }}
      >
        {index + 1}
      </div>
      <div
        style={{
          flex: 1,
          padding: '14px 20px',
          background: 'rgba(255, 255, 255, 0.08)',
          borderRadius: 12,
          fontSize: 22,
          color: 'white',
          border: `2px solid ${color}40`,
        }}
      >
        {step}
      </div>
    </div>
  );
};

// 场景卡片组件
const ScenarioCard: React.FC<{
  scenario: typeof firstAidScenarios[0];
  index: number;
  frame: number;
  fps: number;
}> = ({ scenario, index, frame, fps }) => {
  const delay = index * 90;
  const progress = spring({
    frame: frame - delay,
    fps,
    config: { damping: 15, stiffness: 100 },
  });

  const scale = interpolate(progress, [0, 1], [0.8, 1]);
  const opacity = progress;

  if (frame < delay) return null;

  const isActive = frame >= delay && frame < delay + 85;

  return (
    <div
      style={{
        width: 560,
        padding: 36,
        background: isActive
          ? `linear-gradient(135deg, ${scenario.color}20, ${scenario.color}10)`
          : 'rgba(255, 255, 255, 0.05)',
        borderRadius: 24,
        border: `3px solid ${isActive ? scenario.color : 'rgba(255, 255, 255, 0.1)'}`,
        backdropFilter: 'blur(10px)',
        opacity,
        transform: `scale(${scale})`,
        boxShadow: isActive ? `0 10px 40px ${scenario.color}30` : 'none',
      }}
    >
      {/* 标题区域 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
        <div style={{ fontSize: 48 }}>{scenario.icon}</div>
        <div>
          <h2 style={{ fontSize: 30, margin: 0, marginBottom: 6, color: scenario.color }}>
            {scenario.title}
          </h2>
          <p style={{ fontSize: 22, margin: 0, color: 'rgba(255, 255, 255, 0.6)' }}>
            {scenario.subtitle}
          </p>
        </div>
      </div>

      {/* 步骤列表 */}
      <div style={{ marginBottom: 24 }}>
        {scenario.steps.map((step, stepIndex) => (
          <StepCard
            key={stepIndex}
            step={step}
            index={stepIndex}
            frame={frame}
            fps={fps}
            color={scenario.color}
            startFrame={delay + 20}
          />
        ))}
      </div>

      {/* 提示框 */}
      <div
        style={{
          padding: '16px 20px',
          background: `${scenario.color}15`,
          borderRadius: 12,
          borderLeft: `4px solid ${scenario.color}`,
          marginTop: 18,
        }}
      >
        <div style={{ fontSize: 24, fontWeight: 'bold', color: scenario.color, marginBottom: 6 }}>
          💡 关键提示
        </div>
        <div style={{ fontSize: 22, color: 'rgba(255, 255, 255, 0.8)' }}>{scenario.tip}</div>
      </div>
    </div>
  );
};

// 紧急电话提示组件
const EmergencyNumber: React.FC<{ frame: number; fps: number }> = ({ frame, fps }) => {
  const progress = spring({
    frame: frame - 260,
    fps,
    config: { damping: 15, stiffness: 100 },
  });

  const scale = interpolate(progress, [0, 1], [0.9, 1]);
  const opacity = progress;
  const pulse = Math.sin(frame * 0.2) * 0.05 + 1;

  if (frame < 260) return null;

  return (
    <div
      style={{
          position: 'absolute',
          top: 40,
          right: 80,
          zIndex: 10,
        padding: '30px 50px',
        background: 'linear-gradient(135deg, #EF4444, #DC2626)',
        borderRadius: 20,
        opacity,
        transform: `scale(${scale * pulse})`,
        boxShadow: '0 10px 40px rgba(239, 68, 68, 0.5)',
      }}
    >
      <div style={{ fontSize: 28, fontWeight: 'bold', color: 'white', marginBottom: 8 }}>
        🚨 急救电话
      </div>
      <div style={{ fontSize: 72, fontWeight: 'bold', color: 'white', fontFamily: 'monospace' }}>
        120
      </div>
    </div>
  );
};

// 底部行动号召
const CallToAction: React.FC<{ frame: number; fps: number }> = ({ frame, fps }) => {
  const progress = spring({
    frame: frame - 280,
    fps,
    config: { damping: 15, stiffness: 100 },
  });

  const opacity = progress;

  if (frame < 280) return null;

  return (
    <div
      style={{
        position: 'absolute',
        bottom: 60,
        left: 80,
        right: 80,
        padding: '30px 60px',
        background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.9), rgba(37, 99, 235, 0.9))',
        borderRadius: 20,
        opacity,
        boxShadow: '0 10px 40px rgba(59, 130, 246, 0.3)',
      }}
    >
      <div style={{ fontSize: 32, fontWeight: 'bold', color: 'white', textAlign: 'center' }}>
        ⚠️ 掌握急救技能，关键时刻能救命 | 参加专业培训获取认证
      </div>
    </div>
  );
};

// 主组件
export const FirstAidBasicsAnimation: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const titleOpacity = interpolate(frame, [0, 30], [0, 1]);
  const subtitleOpacity = interpolate(frame, [30, 60], [0, 1]);

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(135deg, #1A1A2E 0%, #16213E 100%)',
        color: 'white',
        fontFamily: 'system-ui',
        overflow: 'hidden',
      }}
    >
      {/* 背景心跳动画 */}
      <div style={{ position: 'absolute', top: -50, right: -50, opacity: 0.3 }}>
        <HeartbeatPulse frame={frame} />
      </div>

      {/* 标题 */}
      <div
        style={{
          position: 'absolute',
          top: 80,
          left: 80,
          opacity: titleOpacity,
        }}
      >
        <h1
          style={{
            fontSize: 68,
            fontWeight: 'bold',
            margin: 0,
            marginBottom: 16,
            background: 'linear-gradient(90deg, #EF4444, #F59E0B)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          急救基础知识
        </h1>
        <p
          style={{
            fontSize: 30,
            color: 'rgba(255, 255, 255, 0.6)',
            margin: 0,
            opacity: subtitleOpacity,
          }}
        >
          Essential First Aid Skills • Save Lives
        </p>
      </div>

      {/* 紧急电话 */}
      <EmergencyNumber frame={frame} fps={fps} />

      {/* 场景卡片 */}
      <div
        style={{
          position: 'absolute',
          top: 260,
          left: 80,
          display: 'flex',
          gap: 40,
        }}
      >
        {firstAidScenarios.map((scenario, index) => (
          <ScenarioCard key={scenario.title} scenario={scenario} index={index} frame={frame} fps={fps} />
        ))}
      </div>


      {/* 底部信息 */}
      <div
        style={{
          position: 'absolute',
          bottom: 12,
          left: 80,
          fontSize: 22,
          color: 'rgba(255, 255, 255, 0.3)',
        }}
      >
        💡 本动画仅供参考，请参加专业急救培训获取认证
      </div>
    </AbsoluteFill>
  );
};
